import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'
import { useAuth } from '../auth.jsx'

/** Avatar with the current title; opens the account menu (cabinet, videos, titles, contact, logout). */
export default function UserMenu() {
  const { t, i18n } = useTranslation()
  const { user, profile, isAdmin, logout } = useAuth()
  const [open, setOpen] = useState(false)
  const box = useRef(null)
  const { pathname } = useLocation()

  // Close on navigation, outside click and Escape.
  const [lastPath, setLastPath] = useState(pathname)
  if (lastPath !== pathname) {
    setLastPath(pathname)
    setOpen(false)
  }
  useEffect(() => {
    if (!open) return
    const onClick = (e) => box.current && !box.current.contains(e.target) && setOpen(false)
    const onKey = (e) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const initial = (user.name || user.email || '?')[0].toUpperCase()
  const items = [
    { to: '/profile', label: t('nav.cabinet'), icon: '👤' },
    { to: '/videos', label: t('cabinet.videos'), icon: '🎬' },
    { to: '/ranks', label: t('cabinet.ranks'), icon: profile?.rank?.emoji ?? '🐾' },
    { to: '/leaderboard', label: t('leaderboard.title'), icon: '🏆' },
    { to: '/contact', label: t('contact.title'), icon: '✉️' },
    ...(isAdmin ? [{ to: '/admin', label: t('nav.admin'), icon: '🛠️' }] : []),
  ]

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('nav.account')}
        className="flex cursor-pointer items-center gap-2 rounded-full border-[1.5px] border-edge bg-surface py-1 pr-3 pl-1 font-extrabold text-ink transition hover:border-ink"
      >
        <span className="flex size-9 items-center justify-center rounded-full bg-teal text-on-teal">{initial}</span>
        {profile?.rank ? <span aria-hidden="true">{profile.rank.emoji}</span> : null}
        <span aria-hidden="true" className="text-sm">▾</span>
      </button>
      {open && (
        <div
          role="menu"
          className="fixed inset-x-4 top-[76px] z-[1100] flex flex-col overflow-hidden rounded-2xl border border-line bg-surface py-2 shadow-[0_18px_40px_rgba(0,0,0,0.18)] sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-64"
        >
          <div className="flex flex-col px-4 pt-1 pb-3">
            <span className="truncate font-extrabold">{user.name}</span>
            <span className="truncate text-sm text-subtle">{user.email}</span>
            {profile ? (
              <span className="pt-1 text-sm font-bold text-teal">
                {profile.rank ? `${profile.rank.emoji} ` : ''}
                {profile.rank ? `${profile.rank.name[i18n.resolvedLanguage] ?? profile.rank.name.en} · ` : ''}
                {profile.feed_count} {t('cabinet.meals', { count: profile.feed_count })}
              </span>
            ) : null}
          </div>
          <div className="h-px bg-line" />
          {items.map((i) => (
            <Link
              key={i.to}
              to={i.to}
              role="menuitem"
              className="flex items-center gap-3 px-4 py-2.5 font-bold text-ink no-underline hover:bg-paper"
            >
              <span aria-hidden="true" className="w-5 text-center">{i.icon}</span>
              {i.label}
            </Link>
          ))}
          <div className="h-px bg-line" />
          <button
            type="button"
            role="menuitem"
            onClick={logout}
            className="flex cursor-pointer items-center gap-3 px-4 py-2.5 text-left font-bold text-ink hover:bg-paper"
          >
            <span aria-hidden="true" className="w-5 text-center">↩</span>
            {t('nav.logout')}
          </button>
        </div>
      )}
    </div>
  )
}
